import { t } from "../../../i18n";
import { formatNumericDate } from "../../../lib/format";
import {
  Button,
  Dialog,
  Money,
  Row,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../ui";
import { useCategoryMaps } from "../hooks/useCategoryMaps";
import type { Expense, Member, Settlement, SettlementCategory } from "../types";

interface ViewInvoiceExpensesDialogProps {
  invoice: Settlement["invoices"][number] | null;
  expenses: Expense[];
  members: Member[];
  categories: Array<{
    id: string;
    name: string;
    icon: string;
    color: string;
    defaultShared: boolean;
  }>;
  currency: string;
  onClose: () => void;
}

export function ViewInvoiceExpensesDialog({
  invoice,
  expenses,
  members,
  categories,
  currency,
  onClose,
}: ViewInvoiceExpensesDialogProps) {
  // useCategoryMaps wants the full SettlementCategory shape (with
  // householdId + matchPatterns). The minimal shape we get here is a
  // structural subset — the maps only read id/name/icon/color anyway,
  // so cast to satisfy the hook signature.
  const { memberMap, categoryMap } = useCategoryMaps(
    members,
    categories as unknown as SettlementCategory[],
  );
  const total = expenses.reduce((sum, e) => sum + Number.parseFloat(e.amount), 0);

  return (
    <Dialog
      open={!!invoice}
      onClose={onClose}
      size="lg"
      title={invoice ? t.settlement.invoices.viewDialog.title(invoice.filename) : ""}
      description={t.settlement.invoices.viewDialog.description(expenses.length)}
      footer={<Button onClick={onClose}>{t.common.close}</Button>}
    >
      {expenses.length === 0 ? (
        <p className="text-ink-500 text-sm">{t.settlement.invoices.viewDialog.empty}</p>
      ) : (
        <Stack gap={3}>
          {/* Card-layout for <sm — read-only liste. */}
          <ul className="divide-y divide-ink-100 sm:hidden">
            {expenses.map((e) => {
              const cat = e.categoryId ? categoryMap.get(e.categoryId) : null;
              const payer = memberMap.get(e.paidByUserId);
              return (
                <li key={e.id} className="flex flex-col gap-1 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex min-w-0 items-baseline gap-2">
                      <span className="shrink-0 text-ink-500 text-xs tabular-nums">
                        {formatNumericDate(e.date)}
                      </span>
                      <span className="truncate text-ink-900 text-sm">{e.description}</span>
                    </div>
                    <span className="shrink-0 font-medium text-sm tabular-nums">
                      <Money amount={e.amount} currency={e.currency} />
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-ink-600 text-xs">
                    {cat ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </span>
                    ) : (
                      <span className="text-ink-400">
                        {t.settlement.expenses.table.uncategorized}
                      </span>
                    )}
                    <span className="text-ink-300">·</span>
                    <span className="truncate">{payer?.displayName ?? payer?.email ?? "—"}</span>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="hidden sm:block">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.settlement.expenses.table.date}</TableHeaderCell>
                  <TableHeaderCell>{t.settlement.expenses.table.description}</TableHeaderCell>
                  <TableHeaderCell>{t.settlement.expenses.table.category}</TableHeaderCell>
                  <TableHeaderCell>{t.settlement.expenses.table.paidBy}</TableHeaderCell>
                  <TableHeaderCell align="right">
                    {t.settlement.expenses.table.amount}
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody className="[&>tr:nth-child(even)]:bg-ink-50/40 dark:[&>tr:nth-child(even)]:bg-ink-100/20">
                {expenses.map((e) => {
                  const cat = e.categoryId ? categoryMap.get(e.categoryId) : null;
                  const payer = memberMap.get(e.paidByUserId);
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-ink-500 tabular-nums">
                        {formatNumericDate(e.date)}
                      </TableCell>
                      <TableCell>
                        <span className="text-ink-900 text-sm">{e.description}</span>
                      </TableCell>
                      <TableCell>
                        {cat ? (
                          <span className="inline-flex items-center gap-1.5 text-ink-700 text-xs">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: cat.color }}
                            />
                            {cat.name}
                          </span>
                        ) : (
                          <span className="text-ink-400 text-xs">
                            {t.settlement.expenses.table.uncategorized}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-ink-600 text-xs">
                        {payer?.displayName ?? payer?.email ?? "—"}
                      </TableCell>
                      <TableCell align="right" className="tabular-nums">
                        <Money amount={e.amount} currency={e.currency} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Row justify="between" className="px-1 text-sm">
            <span className="text-ink-500">{t.settlement.invoices.viewDialog.totalLabel}</span>
            <span className="font-medium text-ink-900 tabular-nums">
              <Money amount={total.toFixed(2)} currency={currency} />
            </span>
          </Row>
        </Stack>
      )}
    </Dialog>
  );
}
